import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface ShopifyConfigurationAlertProps {
  onCompleteSetup: () => void;
}

export function ShopifyConfigurationAlert({
  onCompleteSetup,
}: ShopifyConfigurationAlertProps) {
  const navigate = useNavigate();
  const { t } = useTranslation('common');

  const handleGoToSettings = () => {
    navigate('/settings');
  };

  return (
    <Alert
      variant="destructive"
      className="border-orange-500 bg-orange-50 text-orange-900 dark:bg-orange-950 dark:text-orange-100"
      role="alert"
      aria-live="polite"
      aria-atomic="true"
    >
      <AlertTriangle className="h-5 w-5 text-orange-600 dark:text-orange-400" aria-hidden="true" />
      <AlertTitle className="text-base font-semibold">
        {t('shopifyAlert.title')}
      </AlertTitle>
      <AlertDescription className="mt-2 space-y-3">
        <p className="text-sm">
          <strong>{t('shopifyAlert.critical')}</strong> {t('shopifyAlert.description')}
        </p>
        <div className="flex gap-3 pt-2">
          <Button
            onClick={onCompleteSetup}
            variant="default"
            size="sm"
            className="bg-orange-600 hover:bg-orange-700 text-white dark:bg-orange-500 dark:hover:bg-orange-600"
            aria-label={t('shopifyAlert.completeSetup')}
          >
            {t('shopifyAlert.completeSetup')}
          </Button>
          <Button
            onClick={handleGoToSettings}
            variant="outline"
            size="sm"
            className="border-orange-300 text-orange-900 hover:bg-orange-100 dark:border-orange-700 dark:text-orange-100 dark:hover:bg-orange-900"
            aria-label={t('shopifyAlert.goToSettings')}
          >
            <Settings className="mr-2 h-4 w-4" aria-hidden="true" />
            {t('shopifyAlert.goToSettings')}
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  );
}
